import {
	freeServicesAtom,
	hasPaidServicePricing,
	rawAiCostBreakdownAtom,
	rawSeasonalPricingAtom,
	serviceAvailabilityAtom,
	startingCostAtom,
} from '@client/jotai/storefront/pricing';
import { CtaButton } from '@tkww/orion-web-ds-action';
import { Checkbox } from '@tkww/orion-web-ds-input';
import { TextBody } from '@tkww/orion-web-ds-text';
import { useAtomValue } from 'jotai';
import React from 'react';
import type { PricingEstimationService } from '../../../../types';
import { decisionArea } from '../../../hooks/trackingConstants';
import { useTrackEstimateStepNext } from '../../../hooks/useTrackEstimateStepNext';
import { SERVICES } from '../../../utils/estimateDisplay';
import { ServicesMinimumSpendInfo } from '../../ServicesMinimumSpendInfo';
import Styles from './styles.scss';

export interface ServicesStepProps {
	selectedServices: PricingEstimationService[];
	onChange: (services: PricingEstimationService[]) => void;
	onNext: () => void;
	buttonLabel?: string;
}

export const ServicesStep = ({
	selectedServices,
	onChange,
	onNext,
	buttonLabel = 'Next',
}: ServicesStepProps) => {
	const trackEstimateServicesStepNext = useTrackEstimateStepNext();
	const startingCost = useAtomValue(startingCostAtom);
	const rawSeasonalPricing = useAtomValue(rawSeasonalPricingAtom);
	const rawAiCostBreakdown = useAtomValue(rawAiCostBreakdownAtom);
	const freeServices = useAtomValue(freeServicesAtom);
	const serviceAvailability = useAtomValue(serviceAvailabilityAtom);
	const hasMinimumSpend = Boolean(startingCost);
	const hasPaidSelectedService = hasPaidServicePricing({
		services: selectedServices,
		seasonalPricing: rawSeasonalPricing,
		aiCostBreakdown: rawAiCostBreakdown,
		freeServices,
		serviceAvailability,
	});
	const showMinimumSpendInfo =
		selectedServices.length > 0 && !hasMinimumSpend && !hasPaidSelectedService;
	const canAdvance = selectedServices.length > 0 && !showMinimumSpendInfo;

	const handleChange = (service: PricingEstimationService) => {
		const updated = selectedServices.includes(service)
			? selectedServices.filter((s) => s !== service)
			: [...selectedServices, service];
		onChange(updated);
	};

	const handleNext = () => {
		if (!canAdvance) return;
		trackEstimateServicesStepNext(decisionArea.SERVICES, selectedServices);
		onNext();
	};

	return (
		<div className={Styles.servicesStep} data-testid="services-step">
			<div className={Styles.header}>
				<TextBody size="xl" bold>
					Which services do you need?
				</TextBody>
				<TextBody size="sm" color="subtle-dark">
					Select everything you'd like to include in your estimated venue price.
				</TextBody>
			</div>
			<div className={Styles.checkboxList}>
				{SERVICES.map(({ key, value }) => (
					<label key={key} htmlFor={key} className={Styles.checkboxItem}>
						<Checkbox
							id={key}
							checked={selectedServices.includes(key)}
							onChange={() => handleChange(key)}
							data-testid={`service-checkbox-${key}`}
						/>
						{value}
					</label>
				))}
			</div>
			<div className={Styles.footer}>
				{showMinimumSpendInfo && <ServicesMinimumSpendInfo />}
				<CtaButton
					className={Styles.ctaButton}
					styleType="primary"
					size="lg"
					onClick={handleNext}
					disabled={!canAdvance}
					data-testid="services-next-button"
				>
					{buttonLabel}
				</CtaButton>
			</div>
		</div>
	);
};
